/** @format */

import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContainerWidth } from "mydnv/utils/useContainerWidth";

export const PaginationControl = ({
    contentPerPage,
    totalContentLength,
    onPaginationUpdate,
    isFirstPage,
    setIsFirstPage
}) => {
    const DisplayModes = Object.freeze({
        Full: "full",
        Condensed: "condensed",
    });

    const DisplayStyles = Object.freeze({
        Inline: "inline",
        Stacked: "stacked",
    });

    const DisplayedList = Object.freeze({
        Initial: "initial",
        Middle: "middle",
        Final: "final",
    });

    const paginationContainerRef = useRef(null);
    const pageNumbersContainerRef = useRef(null);
    const firstPageBtnRef = useRef(null);
    const prevPageBtnRef = useRef(null);
    const nextPageBtnRef = useRef(null);
    const lastPageBtnRef = useRef(null);

    let paginationContainerWidth = useContainerWidth(paginationContainerRef);
    let pageNumbersContainerWidth = useContainerWidth(pageNumbersContainerRef);
    let firstPageBtnWidth = useContainerWidth(firstPageBtnRef);
    let prevPageBtnWidth = useContainerWidth(prevPageBtnRef);
    let nextPageBtnWidth = useContainerWidth(nextPageBtnRef);
    let lastPageBtnWidth = useContainerWidth(lastPageBtnRef);
  
    const [canRenderBtnsAndPageNumbers, setCanRenderBtnsAndPageNumbers] =
        useState(false);
    const [paginationNumbersFreeWidth, setPaginationNumbersFreeWidth] =
        useState(0);
    const [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
    const [paginationStartingPosition, setPaginationStartingPosition] =
        useState(1);

    const [paginationLastPage, setPaginationLastPage] = useState(1);
    const [paginationKeysStart, setPaginationKeysStart] = useState([]);
    const [paginationKeysMiddle, setPaginationKeysMiddle] = useState([]);
    const [paginationKeysEnd, setPaginationKeysEnd] = useState([]);

    const [currentListDisplayed, setCurrentListDisplayed] = useState(
        DisplayedList.Initial
    );

    const [paginationNumberListSize, setPaginationNumberListSize] = useState(
        DisplayModes.Full
    );

    const [paginationDisplayStyle, setPaginationDisplayStyle] = useState(
        DisplayStyles.Inline
    );

    useEffect(() =>{
        if(isFirstPage){
            goToFirstPage();
            setIsFirstPage(false);
        }
    })

    useEffect(() => {
        // pixel approximations for pagination long buttons
        let stockButtonSizes = 92 + 120 + 92 + 92;
        let calculatedBtnSizes =
            firstPageBtnWidth +
            prevPageBtnWidth +
            nextPageBtnWidth +
            lastPageBtnWidth;

        // this is needed as on resize, the moment the referenced elements are no longer visable,
        // the value of their combined width is no longer taken into account causing render issues
        // as the code will view the space available as ok to show the buttons once again
        let btnRenderSizePx =
            calculatedBtnSizes > 0 ? calculatedBtnSizes : stockButtonSizes;

        let numbersControlWidth = Math.max(
            0,
            paginationContainerWidth - btnRenderSizePx
        );

        // let canRenderFullPageList =
        //     btnRenderSizePx + pageNumbersContainerWidth <=
        //     paginationContainerWidth;

        // check the icons width for a minimum of 5 icons each at 44px in width
        let paginationIconsMinWidth = 5 * 44;

        // check whether we can render the list with the text buttons if possible
        let canRenderFullPageList =
            btnRenderSizePx + paginationIconsMinWidth <=
            paginationContainerWidth;

        // set whether or not the buttons are able to fully rendered with no issues
        // set what size in pixels are we using to display the page numbers
        setCanRenderBtnsAndPageNumbers(canRenderFullPageList);
        setPaginationNumbersFreeWidth(
            canRenderFullPageList
                ? numbersControlWidth
                : paginationContainerWidth
        );
    }, [
        paginationContainerWidth,
        pageNumbersContainerWidth,
        firstPageBtnWidth,
        prevPageBtnWidth,
        nextPageBtnWidth,
        lastPageBtnWidth,
    ]);

    useEffect(() => {
        setPaginationLastPage(Math.ceil(totalContentLength / contentPerPage));
    }, [totalContentLength, contentPerPage]);

    useEffect(() => {
        // determing how many numbers are possible to render on screen
        let pageNumberSizePx = 44;
        let numButtonsPlaceable = Math.floor(
            paginationNumbersFreeWidth / pageNumberSizePx
        );

        let canDisplayFullNumbersList = numButtonsPlaceable >= 7;

        // set the number of keys maximum for the edge key arrays
        let edgeKeysLimit = canDisplayFullNumbersList ? 5 : (paginationLastPage < 5 ? paginationLastPage : 3);
        let initialArrayKeys = [];
        let middleArrayKeys = [];
        let endArrayKeys = [];

        // set whether or not we show the full numbers list with 7 values displayed total (5 + ellipses/edges)
        setPaginationNumberListSize(
            canDisplayFullNumbersList
                ? DisplayModes.Full
                : DisplayModes.Condensed
        );

        setPaginationDisplayStyle(
            canRenderBtnsAndPageNumbers
                ? DisplayStyles.Inline
                : DisplayStyles.Stacked
        );

        // build the arrays of keys to house the page numbers
        if (paginationLastPage <= edgeKeysLimit) {
            // the number of pages can fit on a normal pagination list display
            initialArrayKeys = Array.from(
                { length: paginationLastPage },
                (_, index) => index + 1
            );
        } else {
            // pages can fit on first and final page keys array
            if (paginationLastPage <= edgeKeysLimit * 2) {
                initialArrayKeys = Array.from(
                    { length: Math.ceil(paginationLastPage / 2) },
                    (_, index) => index + 1
                );

                endArrayKeys = Array.from(
                    { length: Math.floor(paginationLastPage / 2) },
                    (_, index) =>
                        paginationLastPage -
                        Math.floor(paginationLastPage / 2) +
                        index +
                        1
                );
            } else {
                initialArrayKeys = Array.from(
                    { length: edgeKeysLimit },
                    (_, index) => index + 1
                );

                middleArrayKeys = Array.from(
                    { length: paginationLastPage - 2 * edgeKeysLimit },
                    (_, index) => index + 1 + edgeKeysLimit
                );

                endArrayKeys = Array.from(
                    { length: edgeKeysLimit },
                    (_, index) => paginationLastPage - edgeKeysLimit + index + 1
                );
            }
        }

        setPaginationKeysStart(initialArrayKeys);
        setPaginationKeysMiddle(middleArrayKeys);
        setPaginationKeysEnd(endArrayKeys);
    }, [
        paginationLastPage,
        paginationNumbersFreeWidth,
        canRenderBtnsAndPageNumbers,
        DisplayModes.Full,
        DisplayModes.Condensed,
        DisplayStyles.Inline,
        DisplayStyles.Stacked,
    ]);

    useEffect(() => {
        let isPageInInitial = paginationKeysStart.includes(
            paginationCurrentPage
        );

        let isPageInMiddle = paginationKeysMiddle.includes(
            paginationCurrentPage
        );

        setCurrentListDisplayed(
            isPageInInitial
                ? DisplayedList.Initial
                : isPageInMiddle
                    ? DisplayedList.Middle
                    : DisplayedList.Final
        );
    }, [
        paginationCurrentPage,
        paginationKeysStart,
        paginationKeysMiddle,
        DisplayedList.Initial,
        DisplayedList.Middle,
        DisplayedList.Final,
    ]);

    const handlePaginationUpdate = (newStartingPosition, newPageNumber) => {
        setPaginationCurrentPage(newPageNumber);
        setPaginationStartingPosition(newStartingPosition);
        onPaginationUpdate(newStartingPosition);
    };

    const goToSpecificPage = (pageNumber) => {
        let newStartingPosition = (pageNumber - 1) * contentPerPage + 1;
        handlePaginationUpdate(newStartingPosition, pageNumber);
    };

    const goToFirstPage = () => {
        handlePaginationUpdate(1, 1);
    };

    const goToPrevPage = () => {
        let newStartingPosition = paginationStartingPosition - contentPerPage;
        handlePaginationUpdate(newStartingPosition, paginationCurrentPage - 1);
    };

    const goToNextPage = () => {
        let newStartingPosition = paginationStartingPosition + contentPerPage;
        handlePaginationUpdate(newStartingPosition, paginationCurrentPage + 1);
    };

    const goToLastPage = () => {
        let startingBookmarkPosition =
            (paginationLastPage - 1) * contentPerPage + 1;
        handlePaginationUpdate(startingBookmarkPosition, paginationLastPage);
    };

    const shouldDisablePrevPageBtns = () => paginationCurrentPage === 1;
    const shouldDisableNextPageBtns = () =>
        contentPerPage === 0
            ? true
            : paginationCurrentPage ===
              Math.ceil(totalContentLength / contentPerPage);

    return (
        <div className='paginationControl' ref={paginationContainerRef}>
            {paginationDisplayStyle === DisplayStyles.Inline && (
                <>
                    <button
                        ref={firstPageBtnRef}
                        className='firstPageBtn'
                        onClick={goToFirstPage}
                        disabled={shouldDisablePrevPageBtns()}
                    >
                        <FontAwesomeIcon icon='fas fa-chevron-double-left' />
                        <span>First</span>
                    </button>

                    <button
                        ref={prevPageBtnRef}
                        className='previousPageBtn'
                        onClick={goToPrevPage}
                        disabled={shouldDisablePrevPageBtns()}
                    >
                        <FontAwesomeIcon icon='fas fa-chevron-left' />
                        <span>Previous</span>
                    </button>

                    <div
                        className='paginationPageNumberContainer'
                        ref={pageNumbersContainerRef}
                    >
                        {currentListDisplayed === DisplayedList.Initial && (
                            <>
                                {paginationKeysStart.map((pageNum) => (
                                    <div
                                        key={pageNum}
                                        onClick={() =>
                                            goToSpecificPage(pageNum)
                                        }
                                        className={`${
                                            paginationCurrentPage === pageNum
                                                ? "currentPaginationPage"
                                                : ""
                                        }`}
                                    >
                                        {" "}
                                        <span>{pageNum}</span>
                                    </div>
                                ))}

                                {!paginationKeysStart.includes(
                                    paginationLastPage
                                ) && (
                                    <>
                                        <div className='ellipses'>...</div>
                                        <div
                                            onClick={goToLastPage}
                                            className={`${
                                                paginationCurrentPage ===
                                                paginationLastPage
                                                    ? "currentPaginationPage"
                                                    : ""
                                            }`}
                                        >
                                            <span>{paginationLastPage}</span>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {currentListDisplayed === DisplayedList.Middle && (
                            <>
                                <div onClick={goToFirstPage}>
                                    <span>1</span>
                                </div>

                                <div className='ellipses'>...</div>

                                <div
                                    className='prevPageNumber'
                                    onClick={goToPrevPage}
                                >
                                    <span>{paginationCurrentPage - 1}</span>
                                </div>

                                <div className='currentPaginationPage'>
                                    <span>{paginationCurrentPage}</span>
                                </div>

                                <div
                                    className='nextPageNumber'
                                    onClick={goToNextPage}
                                >
                                    <span>{paginationCurrentPage + 1}</span>
                                </div>

                                <div className='ellipses'>...</div>

                                <div onClick={goToLastPage}>
                                    <span>{paginationLastPage}</span>
                                </div>
                            </>
                        )}

                        {currentListDisplayed === DisplayedList.Final && (
                            <>
                                <div onClick={goToFirstPage}>
                                    <span>1</span>
                                </div>

                                <div className='ellipses'>...</div>

                                {paginationKeysEnd.map((pageNum) => (
                                    <div
                                        key={pageNum}
                                        onClick={() =>
                                            goToSpecificPage(pageNum)
                                        }
                                        className={`${
                                            paginationCurrentPage === pageNum
                                                ? "currentPaginationPage"
                                                : ""
                                        }`}
                                    >
                                        <span>{pageNum}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    <button
                        ref={nextPageBtnRef}
                        className='nextPageBtn'
                        onClick={goToNextPage}
                        disabled={shouldDisableNextPageBtns()}
                    >
                        <span>Next</span>
                        <FontAwesomeIcon icon='fas fa-chevron-right' />
                    </button>

                    <button
                        ref={lastPageBtnRef}
                        className='lastPageBtn'
                        onClick={goToLastPage}
                        disabled={shouldDisableNextPageBtns()}
                    >
                        <span>Last</span>
                        <FontAwesomeIcon icon='fas fa-chevron-double-right' />
                    </button>
                </>
            )}

            {paginationDisplayStyle === DisplayStyles.Stacked && (
                <>
                    <div className='stackedPaginationControls'>
                        <div
                            className='paginationPageNumberContainer'
                            ref={pageNumbersContainerRef}
                        >
                            {currentListDisplayed === DisplayedList.Initial && (
                                <>
                                    {paginationKeysStart.map((pageNum) => (
                                        <div
                                            key={pageNum}
                                            onClick={() =>
                                                goToSpecificPage(pageNum)
                                            }
                                            className={`${
                                                paginationCurrentPage ===
                                                pageNum
                                                    ? "currentPaginationPage"
                                                    : ""
                                            }`}
                                        >
                                            {" "}
                                            <span>{pageNum}</span>
                                        </div>
                                    ))}

                                    {!paginationKeysStart.includes(
                                        paginationLastPage
                                    ) && (
                                        <>
                                            <div className='ellipses'>...</div>
                                            <div
                                                onClick={goToLastPage}
                                                className={`${
                                                    paginationCurrentPage ===
                                                    paginationLastPage
                                                        ? "currentPaginationPage"
                                                        : ""
                                                }`}
                                            >
                                                <span>
                                                    {paginationLastPage}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {currentListDisplayed === DisplayedList.Middle && (
                                <>
                                    {paginationNumberListSize ===
                                        DisplayModes.Full && (
                                        <div onClick={goToFirstPage}>
                                            <span>1</span>
                                        </div>
                                    )}
                                    <div className='ellipses'>...</div>

                                    <div
                                        className='prevPageNumber'
                                        onClick={goToPrevPage}
                                    >
                                        <span>{paginationCurrentPage - 1}</span>
                                    </div>

                                    <div className='currentPaginationPage'>
                                        <span>{paginationCurrentPage}</span>
                                    </div>

                                    <div
                                        className='nextPageNumber'
                                        onClick={goToNextPage}
                                    >
                                        <span>{paginationCurrentPage + 1}</span>
                                    </div>

                                    <div className='ellipses'>...</div>
                                    {paginationNumberListSize ===
                                        DisplayModes.Full && (
                                        <div onClick={goToLastPage}>
                                            <span>{paginationLastPage}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {currentListDisplayed === DisplayedList.Final && (
                                <>
                                    <div onClick={goToFirstPage}>
                                        <span>1</span>
                                    </div>

                                    <div className='ellipses'>...</div>

                                    {paginationKeysEnd.map((pageNum) => (
                                        <div
                                            key={pageNum}
                                            onClick={() =>
                                                goToSpecificPage(pageNum)
                                            }
                                            className={`${
                                                paginationCurrentPage ===
                                                pageNum
                                                    ? "currentPaginationPage"
                                                    : ""
                                            }`}
                                        >
                                            <span>{pageNum}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        <div className='paginationCompressedNavBtns'>
                            <div className='paginationNavBtnsLeft'>
                                <button
                                    className='firstPageBtn'
                                    onClick={goToFirstPage}
                                    disabled={shouldDisablePrevPageBtns()}
                                >
                                    <FontAwesomeIcon icon='fas fa-chevron-double-left' />
                                </button>

                                <button
                                    className='previousPageBtn'
                                    onClick={goToPrevPage}
                                    disabled={shouldDisablePrevPageBtns()}
                                >
                                    <FontAwesomeIcon icon='fas fa-chevron-left' />
                                </button>
                            </div>

                            <div className='paginationNavBtnsRight'>
                                <button
                                    className='nextPageBtn'
                                    onClick={goToNextPage}
                                    disabled={shouldDisableNextPageBtns()}
                                >
                                    <FontAwesomeIcon icon='fas fa-chevron-right' />
                                </button>

                                <button
                                    className='lastPageBtn'
                                    onClick={goToLastPage}
                                    disabled={shouldDisableNextPageBtns()}
                                >
                                    <FontAwesomeIcon icon='fas fa-chevron-double-right' />
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
